import { Injectable, OnDestroy } from "@angular/core";
import { ItemTagViewModel, ProductDataModel4, ProductViewModel, ScheduledForRequest, RecipeLinkingDataModel, RecipelinkingViewModel, MenuSettings_SortType } from "../../shared/models";
import { CoreLogService } from "../../services/log.service";
import { UserHelperService } from "../../services/userHelper.service";
import { Observable } from "rxjs";
import { ProductService } from "../../services/product.service";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";
import { WebShopService } from "../../services/webShop.service";
import { CoreLanguageService } from "../../services/language.service";
import { ContentType, SortDirection } from "../../shared/contentTypes";
import { ObjectObserverFactory } from "../../services/objectObserverFactory";
import { IObjectObserver } from "../../services/IObjectObserver";
import { IProductViewModel } from "../../shared/models/product.facade";
import { SortOption } from "../../shared/models/sort.model";
import { PagingHelper } from "../../shared/pagingHelper";
import { ValidatorsConfig } from "../../shared/validator/validator.function";
import Utils from "../../shared/utils";

@Injectable({
  providedIn: "root"
})
export class MenuService implements OnDestroy {

  private recipeLinkingObsServ: IObjectObserver<RecipeLinkingDataModel>;

  constructor(private _coreLogService: CoreLogService,
    private _coreLanguageService: CoreLanguageService,
    private _userHelperService: UserHelperService,
    private _objObsInjectHelper: CircularInjectionHelper,
    private _webshopService: WebShopService,
    private _productService: ProductService) {
    this.recipeLinkingObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<RecipeLinkingDataModel>(ContentType.RecipeLinking, this._objObsInjectHelper);
  }

  ngOnDestroy(): void {
    const me = this;
  }

  public get ObserveChanges(): Observable<void> {
    return this._productService.ProductChanges;
  }

  public GetProducts(locationId: number, itemTags: ItemTagViewModel[] = null, likeIds: number[], dislikeIds: number[]): Promise<IProductViewModel[]> {
    const me = this;
    let isTestUser: boolean = false;
    return new Promise<IProductViewModel[]>(async (resolve, reject) => {
      isTestUser = me._userHelperService.IsTestUser;
      try {
        // Daten ermitlung, die zur prüfung der Zeitsteuerung benötigt werden
        const webshopSettings = await me._webshopService.getWebshopSettingsDataModel();
        const menuSettings = await me._webshopService.getMenuSettings();
        const schedule_req = new ScheduledForRequest(me._webshopService.getScheduledForReferenceTime(), locationId, webshopSettings, me._coreLanguageService);
        // Artikel Laden
        const products = await me._productService.GetProductsByFilter(locationId, me._webshopService.sortiment, model => !model.deleted
          && model.saleable
          && !model.isScheduledFor(schedule_req).hide
        );
        const view_products = me.mapToProductViewModelList(locationId, products, itemTags, likeIds, schedule_req);
        // Rezepturverkettungen Laden
        const currentServiceMode = me._webshopService.getServiceMode();
        const recipeLinkings = await me.recipeLinkingObsServ.GetObjectsByFilter(model => !model.deleted
          && model.use_shop
          && model.isInWebShopMode(currentServiceMode));
        // Rezepturverkettungen nach Sortierung
        const sortFunction = me.getSortFunction(menuSettings.sort_type);
        const view_recipeLinkings = me.mapToRecipeLinkingViewModelList(locationId, recipeLinkings, likeIds, dislikeIds, view_products, sortFunction);
        const hide_products_ids = view_recipeLinkings.reduce((ids, item) => ids.concat(item.hide_product_ids), new Array<number>());

        const result = new Array<IProductViewModel>();
        // nur rezepturverkettung mit rezepturen
        result.push(...view_recipeLinkings.filter(view_recipeLinking => view_recipeLinking.products.length > 0));
        // nur produkte die nicht durch rezepturverkettung ausgeblendet werden
        result.push(...view_products.filter(view_product => !hide_products_ids.includes(view_product.product_id)));

        resolve(result);
      } catch (error) {
        me._coreLogService.error("MenuService.GetProducts", "GetObjectsByFilter", error);
        reject();
      }
    });
  }

  private getSortFunction(sortType: MenuSettings_SortType): (a: IProductViewModel, b: IProductViewModel) => number {
    const sortOptions: SortOption[] = [];
    const pagingHelper = new PagingHelper();

    if (sortType === MenuSettings_SortType.FreeItemnumber) {
      sortOptions.push(pagingHelper.createSortOption("free_item_nr", SortDirection.ASC));
    } else if (sortType === MenuSettings_SortType.Alphabetic) {
      sortOptions.push(pagingHelper.createSortOption("label", SortDirection.ASC));
    }
    // Immer als Fallback
    sortOptions.push(pagingHelper.createSortOption("artikelNo", SortDirection.ASC));

    return pagingHelper.fieldSorter(sortOptions);
  }

  public GetProductsByProductIds(locationId: number, productIds: number[], itemTags: ItemTagViewModel[] = null, likeIds: number[]): Promise<IProductViewModel[]> {
    const me = this;
    let isTestUser: boolean = false;
    return new Promise<ProductViewModel[]>(async (resolve, reject) => {
      isTestUser = me._userHelperService.IsTestUser;
      try {
        // Daten ermitlung, die zur prüfung der Zeitsteuerung benötigt werden
        const webshopSettings = await me._webshopService.getWebshopSettingsDataModel();
        const schedule_req = new ScheduledForRequest(me._webshopService.getScheduledForReferenceTime(), locationId, webshopSettings, me._coreLanguageService);

        //! TODO klären ob es auch rezepturverkettung anzeigen soll

        const products = await me._productService.GetProductsByFilter(locationId, me._webshopService.sortiment, model => !model.deleted && model.saleable && productIds.includes(model.id));
        resolve(me.mapToProductViewModelList(locationId, products, itemTags, likeIds, schedule_req));
      } catch (error) {
        me._coreLogService.error("MenuService.GetProductsByProductIds", "GetObjectsByFilter", error);
        reject();
      }
    });
  }

  // mapping
  private mapToProductViewModelList(locationId: number, modelList: ProductDataModel4[], itemTags: ItemTagViewModel[], likeIds: number[], schedule_req: ScheduledForRequest): ProductViewModel[] {
    const me = this;
    if (modelList) {
      const result: ProductViewModel[] = [];
      modelList.forEach(model => {
        result.push(new ProductViewModel(locationId, model, itemTags, likeIds, schedule_req));
      });
      return result;
    } else {
      return null;
    }
  }

  private mapToRecipeLinkingViewModelList(locationId: number,
    modelList: RecipeLinkingDataModel[],
    likeIds: number[],
    dislikeIds: number[],
    products: ProductViewModel[],
    sortFunction: (a: IProductViewModel, b: IProductViewModel) => number): RecipelinkingViewModel[] {
    const me = this;
    if (modelList) {
      const result: RecipelinkingViewModel[] = [];
      modelList.forEach(model => {
        result.push(new RecipelinkingViewModel(locationId, model, likeIds, dislikeIds, products, sortFunction));
      });
      return result;
    } else {
      return null;
    }
  }


  //! Currently, the only way to determine the validity date of a ticket is by using its name.
  /** Extracts a date in the format DD.MM.YYYY from a label string
   * and converts it into a numeric sortable format (YYYYMMDD).
   * @param label_de
   */
  private extractDateFromLabel(label_de: string): number {
    if (!label_de) return 0;
    const labelLower = label_de.toLowerCase();
    const date = labelLower.match(ValidatorsConfig.PATTERN_DATE_DD_MM_YYYY);
    if (date) {
      const day = parseInt(date[1], 10);
      const month = parseInt(date[2], 10);
      const year = parseInt(date[3], 10);
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        return year * 10000 + month * 100 + day;
      }
    }
    return 0;
  }

  /**
   * Extracts valid dates from product labels
   * Only includes dates where at least one available product exists
   * @returns Array of date ranges with valid dates
   */
  public getProductDates(products: IProductViewModel[]): { from: number, to: number }[] {
    const me = this;
    const ranges: { from: number, to: number }[] = [];
    const uniqueDates = new Set<number>();
    const productsWithoutValidDate: string[] = [];

    products.forEach(product => {
      if (!product.availible) {
        return;
      }
      const extractedDate = me.extractDateFromLabel(product.label);
      if (extractedDate === 0) {
        productsWithoutValidDate.push(`"${product.label}" (ID: ${product.product_id})`);
        return;
      }
      if (!uniqueDates.has(extractedDate)) {
        uniqueDates.add(extractedDate);
        ranges.push({ from: extractedDate, to: extractedDate });
      }
    });

    if (productsWithoutValidDate.length > 0) {
      me._coreLogService.error("MenuService.getProductDates", `${productsWithoutValidDate.length} Product(s) without valid date: ${productsWithoutValidDate.join(', ')}`, null);
    }
    return ranges;
  }

  /**
   * Filter products by selected date (ticket mode)
   * Extracts date from product label and matches against selected date
   * Only returns available products that are not in the past
   */
  public filterProductsByDate(products: IProductViewModel[], selectedDate: Date): IProductViewModel[] {
    const me = this;
    const selectedDateYMD = selectedDate.getFullYear() * 10000
      + (selectedDate.getMonth() + 1) * 100
      + selectedDate.getDate();
    if (Utils.isPastDate(selectedDate)) {
      return [];
    }

    return products.filter(product => {
      const productDate = me.extractDateFromLabel(product.label);
      const isCorrectDate = productDate === selectedDateYMD;
      const isAvailable = product.availible;
      return isCorrectDate && isAvailable;
    });
  }

}